import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../hooks/useAuth'
import { usePermissions } from '../../../hooks/usePermissions'
import { useCart } from '../../../hooks/useCart'
import { FIXED_ROLES } from '../../../constants/permissions'
import Logo from '../../../components/Logo'

function FashionHeader() {
  const navigate = useNavigate()
  const { currentUser, userData, customRole, logout, loadUserData } = useAuth()
  const { hasAdminAccess, getNormalizedRole } = usePermissions()
  const { getCartItemCount } = useCart()
  const [showUserMenu, setShowUserMenu] = useState(false)

  const cartCount = getCartItemCount()
  const normalizedRole = getNormalizedRole()
  const customRoleHasPermissions = !!customRole?.permissions && Object.keys(customRole.permissions).length > 0
  const showAdmin = hasAdminAccess()
    || normalizedRole === FIXED_ROLES.ADMIN
    || normalizedRole === FIXED_ROLES.SUPERADMIN
    || customRoleHasPermissions

  useEffect(() => {
    if (currentUser && !userData) {
      loadUserData(currentUser.uid)
    }
  }, [currentUser, userData, loadUserData])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('[data-fashion-user-menu]')) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('click', handleClickOutside)
    }

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [showUserMenu])

  return (
    <header className="sticky top-0 z-50 w-full backdrop-blur-md bg-[#f8f7f6]/80 border-b border-[#e5e0df] transition-all duration-300">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 h-12 md:h-14 lg:h-16">
          <Logo className="h-full w-auto object-contain" />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <Link className="text-sm font-semibold hover:text-[#c27e70] transition-colors" to="/">
            Inicio
          </Link>
          <Link className="text-sm font-semibold hover:text-[#c27e70] transition-colors" to="/productos">
            Catálogo
          </Link>
          <Link className="text-sm font-semibold hover:text-[#c27e70] transition-colors" to="/ofertas">
            Ofertas
          </Link>
          <Link className="text-sm font-semibold hover:text-[#c27e70] transition-colors" to="/contacto">
            Contacto
          </Link>
        </nav>

        <div className="flex items-center gap-4">
          <Link
            to="/carrito"
            className="relative p-2 hover:bg-[#e8e6e1] rounded-full transition-colors"
            aria-label="Carrito"
          >
            <span className="material-symbols-outlined text-[24px]">shopping_bag</span>
            {cartCount > 0 ? (
              <span className="absolute top-2 right-2 size-2.5 bg-[#c27e70] rounded-full border border-[#f8f7f6]"></span>
            ) : null}
          </Link>

          <div className="relative" data-fashion-user-menu>
            <button
              type="button"
              className="p-2 hover:bg-[#e8e6e1] rounded-full transition-colors"
              onClick={() => currentUser ? setShowUserMenu(v => !v) : navigate('/login')}
              aria-label="Usuario"
            >
              {currentUser && (currentUser.photoURL || userData?.photoURL) ? (
                <img
                  src={currentUser.photoURL || userData?.photoURL}
                  alt={userData?.displayName || 'Usuario'}
                  className="w-6 h-6 rounded-full object-cover"
                />
              ) : (
                <span className="material-symbols-outlined text-[24px]">person</span>
              )}
            </button>

            {showUserMenu && currentUser ? (
              <div className="absolute right-0 top-full mt-2 w-64 rounded-2xl border border-[#e8e6e1] bg-white shadow-lg overflow-hidden">
                <div className="px-4 py-3">
                  <p className="text-sm font-bold">{userData?.displayName || 'Usuario'}</p>
                  <p className="text-xs text-gray-500">{currentUser.email}</p>
                </div>
                <div className="h-px bg-[#eee]"></div>
                <Link
                  to="/perfil"
                  className="flex items-center gap-2 px-4 py-3 text-sm hover:bg-[#f8f7f6]"
                  onClick={() => setShowUserMenu(false)}
                >
                  <span className="material-symbols-outlined text-[20px]">account_circle</span>
                  Mi Perfil
                </Link>
                <Link
                  to="/mis-pedidos"
                  className="flex items-center gap-2 px-4 py-3 text-sm hover:bg-[#f8f7f6]"
                  onClick={() => setShowUserMenu(false)}
                >
                  <span className="material-symbols-outlined text-[20px]">inventory_2</span>
                  Mis Pedidos
                </Link>
                {showAdmin ? (
                  <Link
                    to="/admin"
                    className="flex items-center gap-2 px-4 py-3 text-sm hover:bg-[#f8f7f6]"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <span className="material-symbols-outlined text-[20px]">admin_panel_settings</span>
                    Panel Admin
                  </Link>
                ) : null}
                <div className="h-px bg-[#eee]"></div>
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50"
                  onClick={async () => {
                    await logout()
                    setShowUserMenu(false)
                  }}
                >
                  <span className="material-symbols-outlined text-[20px]">logout</span>
                  Cerrar Sesión
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  )
}

export default FashionHeader
